import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Image } from 'react-bootstrap';
import '../../styles/login.css';
import { useThemeMode } from '../../ThemeContext';
import { authAPI } from '../../helpers/api_service';
import { MessageAlert } from '../Common';
import LoginBanner1 from '../../assets/loginban1.png';
import LoginBanner2 from '../../assets/loginban2.png';
import LoginBanner3 from '../../assets/loginban3.png';


const loginBanners = [LoginBanner1, LoginBanner2, LoginBanner3];

function ForgotPasswordOTP() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [error, setError] = useState('');
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const emailOrUsername = location.state?.emailOrUsername || '';

  // Image slider effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % loginBanners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (timer === 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // Handle OTP input
  const handleOtpChange = (e, idx) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    // Move to next input if value entered
    if (value && idx < 5) {
      document.getElementById(`otp-input-${idx + 1}`).focus();
    }
    // Move to previous input if deleted
    if (!value && idx > 0) {
      document.getElementById(`otp-input-${idx - 1}`).focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!emailOrUsername) {
      setError('Missing email or username. Please go back and try again.');
      return;
    }
    setIsLoading(true);
    try {
      const otp_code = otp.join('');
      const data = await authAPI.verifyOtpForgotPassword({ email_or_username: emailOrUsername, otp_code });
      if (data.status === 'success' && data.data?.reset_token) {
        navigate('/reset-password', { state: { reset_token: data.data.reset_token } });
      } else {
        setError(data.message || 'OTP verification failed.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    if (timer === 0) {
      setTimer(30);
      // TODO: Implement resend OTP API call here
    }
  };

  return (
    <div className={`login-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
      <Container fluid className="login-container">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Image/Overlay */}
          <Col lg={6} className="d-none d-lg-block login-left-bg position-relative">
            <div className="login-slider-img-wrap position-absolute w-100 h-100">
              <Image 
                src={loginBanners[currentBanner]} 
                alt="Login Banner" 
                className="login-slider-img" 
                style={{ filter: 'brightness(1.25)' }}
                fluid
              />
            </div>
            <div className="login-left-overlay"></div>
          </Col>
          {/* Right Side: OTP Form */}
          <Col xs={12} lg={6} className="d-flex align-items-center justify-content-center login-right-bg position-relative">
            <div className="login-blur-bg-top"></div>
            <div className="login-blur-bg-bottom"></div>
            {/* Previous Button */}
            <div style={{ position: 'absolute', top: 32, left: 32, zIndex: 2 }}>
              <span style={{ color: mode === 'dark' ? '#bdbdbd' : '#222', fontWeight: 500, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => navigate(-1)}>
                <span style={{ fontSize: 20, fontWeight: 700, marginRight: 2 }}>&lt;</span> Previous
              </span>
            </div>
            <div className="login-form-wrapper w-100" style={{ maxWidth: '486px' }}>
              <div className="mb-4 text-center" style={{ marginTop: '32px' }}>
                <h2 className="login-title" style={{ color: mode === 'dark' ? '#26c3c0' : '#009391' }}>Verify Your Email</h2>
                <div className="login-subtitle">Enter the 6-digit code sent to your email to continue.</div>
              </div>
              <MessageAlert 
                type="error" 
                show={!!error}
                message={error}
              />
              <Form onSubmit={handleSubmit} autoComplete="off">
                <div className="mb-4 text-center">
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Enter OTP</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        id={`otp-input-${idx}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleOtpChange(e, idx)}
                        style={{
                          width: 48,
                          height: 48,
                          textAlign: 'center',
                          fontSize: 24,
                          borderRadius: 8,
                          border: '1px solid #444',
                          background: 'transparent',
                          color: mode === 'dark' ? '#fff' : '#222',
                        }}
                        className="otp-input"
                        autoFocus={idx === 0}
                      />
                    ))}
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="btn-teal w-100 login-btn d-flex align-items-center justify-content-center mb-4"
                  disabled={isLoading || otp.some(d => d === '')}
                >
                  {isLoading ? 'Submitting...' : 'Submit'}
                </Button>
                <div className="text-center" style={{ color: mode === 'dark' ? '#bdbdbd' : '#444', fontSize: 15 }}>
                  Didn't receive the code?{' '}
                  {timer === 0 ? (
                    <span style={{ color: mode === 'dark' ? '#26c3c0' : '#009391', cursor: 'pointer', fontWeight: 500 }} onClick={handleResend}>
                      You can resend
                    </span>
                  ) : (
                    <span style={{ color: mode === 'dark' ? '#26c3c0' : '#009391', fontWeight: 500 }}>
                      You can resend in 00:{timer.toString().padStart(2, '0')}
                    </span>
                  )}
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ForgotPasswordOTP; 